/**
 * API 封装模块
 * 使用 axios 进行网络请求
 * 包含请求拦截器、响应拦截器和常用请求方法
 */
import axios from "axios";
import qs from 'qs';
import MD5 from "crypto-js/md5";
import i18n from "./i18n";

// UUID 生成函数（兼容 React Native）
const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const SIGN_SECRET = "hello wold";

const toUtf8Bytes = (text) => new TextEncoder().encode(text);

const toHex = (buffer) => {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const md5 = async (text) => {
 return MD5(text).toString()
};

const normalizeBody = (data) => {
  if (data == null || data === "") return "";
  if (typeof data === "string") return data;
  return JSON.stringify(data);
};

const applySignHeaders = async (config) => {
  const ts = Date.now();
  const body = normalizeBody(config.data);
  const sign = await md5(`${body}${SIGN_SECRET}${ts}`);

  config.headers = config.headers || {};
  config.headers["sign"] = sign;
  config.headers["req-time"] = String(ts);

  return config;
};

// Web/H5 环境标识（对应原生端 env 与 BuildConfig.VERSION_NAME）
// 后端按这些头做设备/渠道/版本统计，如取值有要求请在此统一调整
const detectPlatform = () => {
  if (typeof navigator === "undefined") return "web";
  const ua = navigator.userAgent || "";
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  return "web";
};

const env = {
  brand: "web",               // 原生 env.brand（设备品牌），H5 固定为 web
  model: "h5",                // 原生 env.model（设备型号）
  platform: detectPlatform(), // 原生 env.platform，按 UA 判断 android/ios/web
  system: "web",              // 原生 env.system（系统版本）
  channel: "h5",              // 原生 env.channel（渠道）
  version: "0.1.0",           // BuildConfig.VERSION_NAME，与 package.json 保持一致
};

// 统一注入原生端要求的请求头（对应 newBuilder 里的 addHeader 字段）
const applyCommonHeaders = (config) => {
  config.headers = config.headers || {};
  // 非 multipart 上传时才覆盖为 JSON；上传请求由 uploadFile 指定 multipart/form-data
  if (
    !String(config.headers["Content-Type"] || "").includes("multipart/form-data")
  ) {
    config.headers["Content-Type"] = "application/json; charset=utf-8";
  }
  config.headers["user-brand"] = env.brand;
  config.headers["user-model"] = env.model;
  config.headers["user-platform"] = env.platform;
  config.headers["user-system"] = env.system;
  config.headers["user-channel"] = env.channel;
  config.headers["user-version"] = env.version;
};

// 统一后端域名：从环境变量 REACT_APP_API_HOST 读取（.env.development / .env.production）
// 测试环境 https://app-dev-api.oakcreektech.com，商用/生产环境 https://app-api.oakcreektech.com


const API_HOST =
  process.env.REACT_APP_API_HOST || "https://app-api.oakcreektech.com";
// 运控平台 baseurl（网关按 /cmtp/ 前缀路由），统一直连后端域名
const CMTP_BASE_URL = `${API_HOST}/cmtp/`;
//登录相关，java平台base url

const LOGIN_BASE_URL = API_HOST;
/**
 * 创建 axios 实例
 * @type {axios.AxiosInstance}
 */
const instance = axios.create({
  baseURL: CMTP_BASE_URL,
  timeout: 10000, // 请求超时设置
  headers: {
    "Content-Type": "application/json", // 默认请求头
  },
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(params, { arrayFormat: "brackets", encode: false }),
  },
});
const login = axios.create({
  baseURL: LOGIN_BASE_URL, // 登录/支付/计费平台，统一域名
  timeout: 10000, // 请求超时设置
  headers: {
    "Content-Type": "application/json", // 默认请求头
  },
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(params, { arrayFormat: "brackets", encode: false }),
  },
});

/**
 * 请求拦截器
 * 1. 自动注入 token
 * 2. 打印请求信息
 */
const getStoredToken = () => {
  return localStorage.getItem('auth_token') || '';
};


instance.interceptors.request.use(
  async (config) => {
    config.headers["x-request-id"] = generateUUID();

    // 自动注入登录 token（如果存在）
    const token = getStoredToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // 给所有业务接口请求添加签名头（非登录接口也需要时，后端会用它做校验）
    await applySignHeaders(config);

    // console.log('Request Interceptor:', config);  // 打印请求信息

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

login.interceptors.request.use(
  async (config) => {
    // 统一注入原生端要求的请求头（user-brand / user-platform / ... 等）
    config.headers = config.headers || {};
    applyCommonHeaders(config);

    // 统一注入当前语言（单一数据源：内存 i18n.language，兜底 localStorage，再兜底 zh）
    const rawLang = i18n.language || localStorage.getItem('locale');
    config.headers["language"] = rawLang === 'en' ? 'en' : 'zh';

    // 自动注入登录 token（统一走 Authorization: Bearer $TOKEN）
    const token = getStoredToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // 便于排查：token 是否注入、请求地址是什么
    console.log("[login request]", {
      url: config.url,
      baseURL: config.baseURL,
      tokenInjected: !!token,
      tokenPrefix: token ? `${token.slice(0, 6)}...` : "",
      params: config.params,
      data: config.data,
    });

    await applySignHeaders(config);

    // console.log('Request Interceptor:', config);  // 打印请求信息

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * 响应拦截器
 * 1. 处理成功响应，返回响应数据
 * 2. 处理错误响应，根据状态码做不同的处理
 * 3. 统一错误处理
 */
instance.interceptors.response.use(
  (response) => {
    // 根据不同的状态码做不同的处理
    if (response.status >= 200 && response.status < 299) {
      // 请求成功，返回服务器的响应数据
      // console.log('Response Interceptor:', response);
      const body = response.data;
      // 后端统一返回 { retCode, retDesc, data } 信封：
      // retCode 存在时，非 "000000" 视为业务失败；成功则直接返回业务数据 data
      if (body && typeof body === "object" && body.retCode !== undefined) {
        if (String(body.retCode) !== "000000") {
          return Promise.reject(new Error(body.retDesc || "请求失败"));
        }
        return body.data;
      }
      return body;
    } else {
      // 如果状态码不是 200，抛出错误
      return Promise.reject(
        new Error(`Unexpected response status: ${response.status}`),
      );
    }
  },
  (error) => {
    // 统一错误处理
    console.error("Response Interceptor Error:", error);

    if (error.response) {
      // 判断是否是 HTTP 错误，且有响应
      const { status, data } = error.response;

      // 根据不同的状态码做相应的处理
      if (status === 401) {
        // 401 Unauthorized：未授权
        window.location.href = "/login";
      } else if (status === 500) {
        // 500 Server Error
        console.error("Server error, please try again later!");
      } else {
        // 其他错误
        console.error(data?.message || "An error occurred!");
      }
    } else if (error.request) {
      // 请求已经发出去，但是没有响应
      console.error("No response from server!");
    } else {
      // 其他错误
      console.error("Request failed: " + error.message);
    }

    return Promise.reject(error);
  },
);
/**
 * 封装 GET 请求方法
 * @param {string} url - 请求地址
 * @param {Object} params - 请求参数
 * @returns {Promise} - 返回 Promise 对象
 */
const get = (url, params = {}) => {
  return instance.get(url, { params }).catch((error) => {
    console.error("GET request error:", error);
    return Promise.reject(error);
  });
};

/**
 * 封装 POST 请求方法
 * @param {string} url - 请求地址
 * @param {Object} data - 请求数据
 * @param {Object} config - axios 配置（用于按需覆盖 headers / params / timeout 等）
 * @returns {Promise} - 返回 Promise 对象
 */
const post = (url, data = {}, config = {}) => {
  return instance.post(url, data, config).catch((error) => {
    console.error("POST request error:", error);
    return Promise.reject(error);
  });
};


/**
 * 封装 PUT 请求方法
 * @param {string} url - 请求地址
 * @param {Object} data - 请求数据
 * @param {Object} config - axios 配置（用于按需覆盖 headers / params / timeout 等）
 * @returns {Promise} - 返回 Promise 对象
 */
const put = (url, data = {}, config = {}) => {
  return instance.put(url, data, config).catch((error) => {
    console.error("PUT request error:", error);
    return Promise.reject(error);
  });
};

/**
 * 封装 PATCH 请求方法
 * @param {string} url - 请求地址
 * @param {Object} data - 请求数据
 * @param {Object} config - axios 配置（用于按需覆盖 headers / params / timeout 等）
 * @returns {Promise} - 返回 Promise 对象
 */
const patch = (url, data = {}, config = {}) => {
  return instance.patch(url, data, config).catch((error) => {
    console.error("PATCH request error:", error);
    return Promise.reject(error);
  });
};

/**
 * 封装 PUT 请求上传文件的方法
 * @param {string} url - 上传URL
 * @param {Object} file - 要上传的文件
 * @param {string} fileType - 文件类型，默认为'video/mp4'
 * @param {Function} onProgress - 上传进度回调函数
 * @returns {Promise} - 返回 Promise 对象
 */
const uploadFile = (url, file, fileType = "video/mp4", onProgress) => {
  // 创建一个 FormData 对象来封装文件
  const formData = new FormData();
  formData.append("file", file);

  // 设置请求头
  const headers = {
    "Content-Type": "multipart/form-data",
    "File-Type": fileType, // 自定义文件类型头部，可以根据需要设置
  };

  // 发送 PUT 请求上传文件
  return instance
    .put(url, formData, {
      headers,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          if (onProgress) {
            onProgress(progress); // 调用上传进度回调
          }
        }
      },
    })
    .catch((error) => {
      console.error("Upload error:", error);
      return Promise.reject(error);
    });
};

/**
 * 获取商品列表
 * GET /cmtp/api/v1/product-list
 * @param {Object} params - 请求参数
 * @param {number} params.page_size - 每页数量（必填）
 * @param {number} params.page_index - 页码（必填）
 * @param {"simulation"|"model"} params.training_entrance - 训练入口类型（必填）
 * @returns {Promise<{products: Array<{productName: string, description: string, product_id: string}>}>}
 */
const listProduct = (params = {}) => {
  return get("/api/v1/product-list", params);
};

/**
 * 计算商品报价（消耗的橡树果数量）
 * GET /cmtp/api/v1/product-quote
 * @param {Object} params - 请求参数
 * @param {string} params.media_id - 媒体ID（必填）
 * @param {string} params.product_id - 商品ID（必填）
 * @returns {Promise<{quote: number}>} - quote 为报价（橡树果数量）
 */
const getProductQuote = (params = {}) => {
  return get("/api/v1/product-quote", params);
};

/**
 * 查询橡树果余额
 * GET /billing/api/v1/account（Java 平台）
 * @returns {Promise<{availableAmount: number}>} - availableAmount 为可用余额
 */
const getAcornBalance = () => {
  return api.login.get("/billing/api/v1/account");
};

/**
 * 发送邮箱验证码
 * POST /auth/api/v1/email/send（Java 平台）
 * @param {Object} params - 请求参数
 * @param {string} params.email - 邮箱
 * @param {string} params.type - 验证码类型（如 login）
 * @returns {Promise<{time: number, message: string, status: boolean}>}
 */
const sendEmailCode = (params = {}) => {
  return api.login.post("/auth/api/v1/email/send", params);
};

/**
 * 邮箱登录
 * POST /auth/api/v1/email/login（Java 平台）
 * @param {Object} params - 请求参数
 * @param {string} params.email - 邮箱
 * @param {string} params.verCode - 邮箱验证码
 * @returns {Promise<{token: string, userInfo: Object, shopInfo: null, isNewUser: boolean}>}
 */
const loginByEmail = (params = {}) => {
  return api.login.post("/auth/api/v1/email/login", params);
};

/**
 * 获取媒体资源下载地址（源视频 / 封面 / 音频）
 * GET /cmtp/api/v1/media/{media_id}/download
 * @param {string} mediaId - 媒体ID（必填，uuid）
 * @param {"source"|"cover"|"audio"} resourceType - 资源类型，默认 "audio"
 * @returns {Promise<{download_url: string, expired_in: number}>} - data 里的下载地址与有效期
 */
const getMediaDownloadUrl = async (mediaId, resourceType = "audio") => {
  // 响应已由 instance 拦截器统一解包，这里直接返回业务数据
  return get(`/api/v1/media/${mediaId}/download`, {
    resource_type: resourceType,
  });
};

// 导出请求方法
const api = {
  get,
  post,
  put,
  patch,
  uploadFile,
  delete: (url, config = {}) => {
    return instance.delete(url, config).catch((error) => {
      console.error('DELETE request error:', error);
      return Promise.reject(error);
    });
  },
  login: {
    get: (url, params = {}, config = {}) => {
      return login
        .get(url, { ...config, params })
        .then(res=>{
          let result = res.data
          if(result.retCode !== "000000"){
            console.log("[login:post] error:", result);
            return Promise.reject(result.retDesc);
          }
          console.log("[login:post] success:", result);
          return result.data;
        })
        .catch((error) => {
          console.error("Login GET request error:", error);
          return Promise.reject(error);
        });
    },
    post: (url, data = {}, config = {}) => {
      return login.post(url, data, config).then(res=>{
        let result = res.data
        if(result.retCode !== "000000"){
          console.log("[login:post] response:", result);
          return Promise.reject(result.retDesc);
        }
        console.log("[login:post] success:", result);
        return result.data;
      }).catch((error) => {
        console.error("Login POST request error:", error);
        return Promise.reject(error);
      });
    },
  },
  listProduct,
  getProductQuote,
  getAcornBalance,
  sendEmailCode,
  loginByEmail,
  getMediaDownloadUrl,
};

export default api ;
